import {
  Component,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CreateAssetCommand } from '@assetaccountant/api/models/assets/create-asset.command';
import { GetAssetResponse } from '@assetaccountant/api/models/assets/get-asset.response';
import { ListFieldsResponse } from '@assetaccountant/api/models/fields/list-fields.response';
import { RegimeAttributesUpdateRequest } from '@assetaccountant/api/models/regime-attributes-update.request';
import { SaveAssetType } from '@assetaccountant/api/models/save-asset-type';
import { RegisterV2Api } from '@assetaccountant/api/services/register-v2.api';
import { RegisterApi } from '@assetaccountant/api/services/register.api';
import { Asset, AssetRegime, AssetRegimeSetting } from '@assetaccountant/assets/models/asset';
import { CreateNewAssetForm } from '@assetaccountant/assets/models/edit-asset-form';
import { withAllocationsToDictionary } from '@assetaccountant/assets/models/field-form-model';
import { Register } from '@assetaccountant/core';
import {
  HasChanges,
  hasChangesOnBeforeUnloadListener,
} from '@assetaccountant/core/guards/has-changes';
import { RegimeDepreciationMethodsDtoWithKey } from '@assetaccountant/core/models/depreciation-method';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { CustomFieldFormService } from '@assetaccountant/core/services/custom-field-form.service';
import { DepreciationService } from '@assetaccountant/core/services/depreciation.service';
import { EnabledPool, PoolsService } from '@assetaccountant/core/services/pools.service';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { TypedFormGroup } from '@assetaccountant/core/utils/typed-forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { EditAssetDetailsComponent } from '../../edit-asset-details/edit-asset-details.component';

@Component({
  selector: 'app-new-asset-details',
  templateUrl: './new-asset-details.component.html',
  styleUrls: ['./new-asset-details.component.scss'],
  standalone: false,
})
export class NewAssetDetailsComponent implements OnInit, OnDestroy, HasChanges {
  @Input() formGroup: TypedFormGroup<CreateNewAssetForm>;
  @Input() parentAsset: GetAssetResponse | null;
  @Input() classificationDefaults?: { [key: string]: string };

  @ViewChild(EditAssetDetailsComponent) editAssetDetailsComponent: EditAssetDetailsComponent;

  asset = new Asset();
  depreciationMethods?: RegimeDepreciationMethodsDtoWithKey[];
  listFieldsResponse?: ListFieldsResponse;
  loaded = false;
  poolsByRegime: Record<number, EnabledPool[]> = {};
  register: Register;
  saveDepreciationSettings = false;

  get hasGroupDefaultSettings() {
    const regimes = this.editAssetDetailsComponent?.currentGroup?.assetGroupRegimes;
    let hasSettings = false;

    regimes?.forEach(regime => {
      if (regime.depreciationMethodId) {
        hasSettings = true;
      }
    });

    return hasSettings;
  }

  get showSettings() {
    return !!(
      this.editAssetDetailsComponent?.currentGroup && this.formGroup?.controls?.firstUseDate.value
    );
  }

  private subscriptions = [
    this.route.data.subscribe(({ register }) => {
      this.register = register;
      this.asset.regimes = register.regimes.map(
        r =>
          ({
            regimeId: r.id,
            regimeName: r.name,
            settings: [] as AssetRegimeSetting[],
          }) as AssetRegime,
      );
    }),
  ];

  constructor(
    public ngbModal: NgbModal,
    private depreciationService: DepreciationService,
    private poolsService: PoolsService,
    private registerApi: RegisterApi,
    private registerV2Api: RegisterV2Api,
    private customFieldFormService: CustomFieldFormService,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
  ) {}

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload = hasChangesOnBeforeUnloadListener;

  get hasChanges() {
    return this.formGroup.dirty;
  }

  ngOnInit() {
    forkJoin([
      this.depreciationService.getDepreciationMethods(),
      this.poolsService.getEnabledPools(),
      this.registerApi.getFirstDayOfCurrentFinancialYear(),
      this.registerV2Api.listFields(),
    ]).subscribe(([methods, pools, , fields]) => {
      this.depreciationMethods = methods;
      pools.forEach(p => {
        this.poolsByRegime[p.regimeId] = this.poolsByRegime[p.regimeId] || [];
        this.poolsByRegime[p.regimeId].push(p);
      });
      this.listFieldsResponse = fields;

      if (methods.some(r => r.hasTaxableUse)) {
        this.formGroup.controls.taxableUse.enable();
      }
      this.loaded = true;
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  getAssetToSave(): CreateAssetCommand & {
    regimes: Array<RegimeAttributesUpdateRequest & AssetRegime>;
  } {
    return {
      ...this.asset,
      ...this.formGroup.value,
      groupId: this.formGroup.value.groupId || 0,
      tags: this.customFieldFormService.getFormCustomFields(this.formGroup.controls.tags),
      fields: this.formGroup.value.fields.map(f => withAllocationsToDictionary(f)),
      assetType: SaveAssetType.Asset,
      regimes: this.formGroup.value.regimes.map(r => ({
        ...r,
      })),
    };
  }
}
